import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { API, axios } from '../../utils/api';
import { dichiarazioneAgenzia, euro, passiOfferte } from '../../utils/scheda';
import { formatEuro, leggiData, leggiDataUtc, linkChiama, ora } from '../../utils/percorso';
import { euroMq, testoOmi, testoScostamento, testoZona, variazioniPrezzo } from '../../utils/prezzoZona';
import { usePrezzoZona } from '../features/PrezzoZona';
import Icona from '../cliente/Icone';
import AzioniVisita from '../staff/AzioniVisita';
import { usePersone } from '../staff/Persone';
import { etichettaEsito, FotoVisita, MOTIVI_VISITA } from '../scheda/TabVisite';
import { MotiviVisita } from '../staff/CardVoce';
import { LogoAgenzia } from '../scheda/TabImmobili';
import { BottoneAsta, BottoneNonAsta, PercheManca } from '../scheda/Ricerche';
import { visitaDa } from '../../utils/visite';

// I riquadri della scheda immobile dello staff (pages/staff/SchedaImmobile.jsx): parere, visite, proposta, storia,
// agenzia con il commento, note interne, prezzo e zona, "se la compra", proponi ad altri clienti.
// "Solo per noi" = mai visibile al cliente (note interne, commento sull'agenzia, voci interne della storia).
// In "Proponi ad altri clienti" c'è «È un'asta» (backend/aste_apprendimento.py): se il filtro delle aste l'ha
// lasciata passare la segna lo staff, la casa esce da tutte le proposte e l'agenzia viene contata.

const errore = (e, riserva = 'Non riuscito') => toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : riserva);
const MESI_BREVI = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'];
const giornoBreve = (d) => (d ? `${d.getDate()} ${MESI_BREVI[d.getMonth()]}` : '');
const righe = (testo) => String(testo || '').split('\n').map((r) => r.trim()).filter(Boolean);
export const SoloPerNoi = () => <span className="tag t-b solo-noi">solo per noi</span>;

// ---------------------------------------------------------------- parere (lo vede il cliente)

const MAX_BREVE = 140;
export const ParereStaff = ({ immobile, onFatto }) => {
  const [form, setForm] = useState(null);
  const [attesa, setAttesa] = useState(false);
  const zona = usePrezzoZona(immobile.id, Boolean(form));
  const pro = immobile.parere_pro || [];
  const contro = immobile.parere_contro || [];
  const vuoto = !immobile.parere && !immobile.parere_breve && !pro.length && !contro.length;
  const apri = () => setForm({
    parere_breve: immobile.parere_breve || '', parere: immobile.parere || '', pro: pro.join('\n'), contro: contro.join('\n'),
    spese_mensili: immobile.spese_mensili != null ? String(immobile.spese_mensili) : '', bagni: immobile.bagni || '', piano: immobile.piano || '',
    classe_energetica: immobile.classe_energetica || '',
  });
  const campo = (k) => ({ value: form[k] ?? '', onChange: (e) => setForm((f) => ({ ...f, [k]: e.target.value })) });
  const salva = async (e) => {
    e.preventDefault();
    setAttesa(true);
    try {
      await axios.put(`${API}/immobili/${immobile.id}/parere`, {
        parere_breve: form.parere_breve, parere: form.parere, parere_pro: righe(form.pro), parere_contro: righe(form.contro),
        spese_mensili: form.spese_mensili, bagni: form.bagni, piano: form.piano, classe_energetica: form.classe_energetica,
      });
      toast.success('Parere salvato: il cliente lo vede nella scheda');
      setForm(null);
      onFatto();
    } catch (err) { errore(err, 'Parere non salvato'); } finally { setAttesa(false); }
  };
  const autore = immobile.parere_autore_nome;
  const data = leggiDataUtc(immobile.parere_data);
  return (
    <section className="card hero" data-testid="parere-staff">
      <div className="ttl"><div className="i"><Icona nome="cuore" /></div><h2>Parere per il cliente</h2>
        {!form && <button type="button" className="link" onClick={apri}>{vuoto ? '+ Scrivi il parere' : 'Modifica'}</button>}</div>
      {!form && vuoto && <p className="vuoto">Nessun parere: il cliente non vede la card. La frase breve compare nel suo elenco.</p>}
      {!form && !vuoto && (
        <>
          {immobile.parere_breve && <p className="breve">“{immobile.parere_breve}”</p>}
          {immobile.parere && <p className="testo-parere">{immobile.parere}</p>}
          {(pro.length > 0 || contro.length > 0) && (
            <div className="pro-contro">
              {pro.length > 0 && <div className="si"><b>Perché sì</b><ul>{pro.map((v) => <li key={v}>{v}</li>)}</ul></div>}
              {contro.length > 0 && <div className="no"><b>Da valutare</b><ul>{contro.map((v) => <li key={v}>{v}</li>)}</ul></div>}
            </div>
          )}
          {(autore || data) && <p className="imp-nota">{[autore, data ? `aggiornato il ${giornoBreve(data)}` : null].filter(Boolean).join(' · ')}</p>}
        </>
      )}
      {form && (
        <form onSubmit={salva} data-testid="modulo-parere">
          {zona?.mq_immobile && <p className="imp-nota" style={{ marginBottom: '.6rem' }}>{[euroMq(zona.mq_immobile),
            zona.scostamento_zona != null && testoScostamento(zona.scostamento_zona)].filter(Boolean).join(' · ')}</p>}
          <div className="imp-campi">
            <label className="largo">Frase breve (elenco del cliente) · {form.parere_breve.length}/{MAX_BREVE}
              <input maxLength={MAX_BREVE} placeholder="Nel vostro budget, zona che vi piaceva." {...campo('parere_breve')} /></label>
            <label className="largo">Parere<textarea rows={4} placeholder="Piano alto, doppia esposizione, condominio piccolo e ben tenuto…" {...campo('parere')} /></label>
            <label>Perché sì (una per riga)<textarea rows={4} {...campo('pro')} /></label>
            <label>Da valutare (una per riga)<textarea rows={4} {...campo('contro')} /></label>
            <label>Spese €/mese<input inputMode="decimal" {...campo('spese_mensili')} /></label>
            <label>Bagni<input {...campo('bagni')} /></label>
            <label>Piano<input {...campo('piano')} /></label>
            <label>Classe<input {...campo('classe_energetica')} /></label>
          </div>
          <div className="imp-azioni">
            <button type="submit" className="btn sm p" disabled={attesa}>{attesa ? 'Salvo…' : 'Salva il parere'}</button>
            <button type="button" className="btn sm" onClick={() => setForm(null)}>Annulla</button>
          </div>
        </form>
      )}
    </section>
  );
};

// ---------------------------------------------------------------- visite (con foto ed esito del cliente)

export const VisiteImmobile = ({ immobile, visite, cliente, onFatto }) => {
  const { perId } = usePersone();
  const adesso = new Date();
  const ordinate = [...visite].sort((a, b) => leggiData(b.data_visita) - leggiData(a.data_visita));
  const future = visite.filter((v) => !v.annullata && leggiData(v.data_visita) > adesso);
  const bene = visite.filter((v) => v.esito_visita === 'positivo').length;
  const male = visite.filter((v) => v.esito_visita === 'negativo').length;
  // «Andata male» qui mandava l'esito **senza motivi** e il server rispondeva 400 («Scegliete almeno un motivo»)
  // con nessun motivo da scegliere in pagina: da questa scheda l'esito negativo non si è mai potuto salvare
  // (segnalato dallo staff il 09/10/2026). Adesso apre la stessa finestra di Oggi e dell'agenda.
  const [chiedeMotivo, setChiedeMotivo] = useState(null);
  const esito = async (v, valore) => {
    if (valore === 'negativo') { setChiedeMotivo(v.id); return; }
    try { await axios.put(`${API}/visite/${v.id}`, { effettuata: true, esito_visita: valore }); toast.success('Esito segnato'); onFatto(); } catch (e) { errore(e); }
  };
  return (
    <section className="card" data-testid="visite-staff">
      <div className="ttl"><div className="i g"><Icona nome="calendario" /></div><h2>Visite</h2></div>
      <div className="kv kv4" style={{ marginBottom: '1rem' }}>
        <div><small>Totale</small><b>{visite.length}</b></div>
        <div><small>Da fare</small><b>{future.length}</b></div>
        <div><small>Andate bene</small><b className="g">{bene}</b></div>
        <div><small>Andate male</small><b className={male ? 'no' : undefined}>{male}</b></div>
      </div>
      {!ordinate.length && <p className="vuoto">Nessuna visita per questa casa.</p>}
      <div className="vis">
        {ordinate.map((v) => {
          const d = leggiData(v.data_visita);
          const chi = perId[v.accompagna_id];
          const futura = d > adesso && !v.annullata;
          const testoEsito = v.esito_visita === 'positivo' ? <b style={{ color: 'var(--ok)' }}>ci piace</b>
            : v.esito_visita === 'negativo' ? <b style={{ color: 'var(--bad)' }}>non fa per lui</b> : null;
          return (
            <div key={v.id} data-testid={`visita-${v.id}`} style={{ gridTemplateColumns: 'auto minmax(0,1fr) auto' }}>
              <div className="h">{d.getDate()}<small>{MESI_BREVI[d.getMonth()]}</small></div>
              <div style={{ minWidth: 0 }}>
                <h4>{futura ? `In programma alle ${ora(d)}` : `Visita alle ${ora(d)}`}{chi ? ` con ${chi.nome}` : ''}</h4>
                {v.annullata ? <div className="m"><b style={{ color: 'var(--bad)' }}>Annullata</b>{v.nota_annullamento ? ` · “${v.nota_annullamento}”` : ''}</div>
                  : testoEsito ? <div className="m">{etichettaEsito(v)}: {testoEsito}{(v.motivi || []).length ? ` · ${v.motivi.map((m) => MOTIVI_VISITA[m] || m).join(', ')}` : ''}{v.note_esito ? ` · “${v.note_esito}”` : ''}</div>
                    : futura ? (v.note_prima ? <div className="m">“{v.note_prima}”</div> : null) : <div className="m">Esito non ancora dato</div>}
                {(v.foto || []).length > 0 && <div className="th">{v.foto.map((f) => <FotoVisita key={f.id} visitaId={v.id} fotoId={f.id} />)}</div>}
              </div>
              <div className="azioni-visita">
                {futura && (
                  <AzioniVisita visita={visitaDa({ id: v.id, data_visita: v.data_visita, durata: v.durata_minuti, accompagna_id: v.accompagna_id }, immobile.id)}
                    cliente={cliente} onFatto={onFatto} />
                )}
                {!futura && !v.annullata && !v.esito_visita && (
                  <>
                    <button type="button" className="btn xs" onClick={() => esito(v, 'positivo')}>Andata bene</button>
                    <button type="button" className="btn xs rosso-vuoto" onClick={() => esito(v, 'negativo')}>Andata male</button>
                  </>
                )}
                {chiedeMotivo === v.id && (
                  <MotiviVisita visitaId={v.id} onChiudi={() => setChiedeMotivo(null)} onFatto={() => { setChiedeMotivo(null); onFatto(); }} />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

// ---------------------------------------------------------------- proposta su questa casa

export const PropostaImmobile = ({ proposta, clienteId }) => {
  if (!proposta) return null;
  const STATI = { inviata: 'in attesa del venditore', in_negoziazione: 'in trattativa', accettata: 'accettata', rifiutata: 'rifiutata', ritirata: 'ritirata', da_definire: 'da presentare' };
  const scade = proposta.data_scadenza ? new Date(proposta.data_scadenza) : null;
  return (
    <section className="card" data-testid="proposta-immobile">
      <div className="ttl"><div className="i"><Icona nome="euro" /></div><h2>Proposta <small>{STATI[proposta.stato] || proposta.stato}</small></h2>
        <Link to={`/admin/clienti/${clienteId}#proposte`}>Apri →</Link></div>
      <p className="imp-nota">Offerto {euro(proposta.prezzo_offerto) || '—'}{proposta.prezzo_richiesto ? ` su ${euro(proposta.prezzo_richiesto)}` : ''}
        {scade && ['inviata', 'in_negoziazione'].includes(proposta.stato) ? ` · scade il ${giornoBreve(scade)}` : ''}</p>
      {(proposta.storico_offerte || []).length > 0 && (
        <div className="timeline">{passiOfferte(proposta.storico_offerte).map((o, n) => <span key={n} className={o.classe}>{o.testo}{o.prezzo && <> <b>{o.prezzo}</b></>}</span>)}</div>
      )}
    </section>
  );
};

// ---------------------------------------------------------------- storia (anche le voci interne)

export const StoriaStaff = ({ storico }) => (
  <section className="card" data-testid="storico-immobile">
    <div className="ttl"><div className="i"><Icona nome="orologio" /></div><h2>Storia di questa casa <small>{storico.length}</small></h2></div>
    {!storico.length && <p className="vuoto">Nessuna attività registrata.</p>}
    <div className="feed">
      {storico.map((e, i) => {
        const d = leggiDataUtc(e.data_evento);
        const interna = e.metadata?.solo_staff;
        return (
          <div key={e.id || i} data-testid={`storico-${i}`}>
            <i className={`dot ${interna ? 'b' : e.tipo_evento?.startsWith('visita') ? 'g' : 'k'}`} />
            <div style={{ minWidth: 0 }}>
              <b>{e.descrizione}{interna && <> <SoloPerNoi /></>}</b>
              <span>{[d ? `${giornoBreve(d)} · ${ora(d)}` : null, e.utente_nome ? `da ${e.utente_nome}` : null,
                e.stato_precedente && e.stato_nuovo ? `${e.stato_precedente.replace(/_/g, ' ')} → ${e.stato_nuovo.replace(/_/g, ' ')}` : null,
                e.metadata?.note ? `“${e.metadata.note}”` : null].filter(Boolean).join(' · ')}</span>
            </div>
          </div>
        );
      })}
    </div>
  </section>
);

// ---------------------------------------------------------------- agenzia e commento (solo per noi)

const FEEDBACK = [['', 'Nessun giudizio'], ['positivo', 'Bene'], ['neutro', 'Così così'], ['negativo', 'Male']];
export const AgenziaImmobile = ({ immobile, agenzia, commento, isAdmin, onFatto }) => {
  const [dati, setDati] = useState({ feedback: commento?.feedback || '', nota: commento?.nota || '' });
  const [attesa, setAttesa] = useState(false);
  useEffect(() => { setDati({ feedback: commento?.feedback || '', nota: commento?.nota || '' }); }, [commento?.feedback, commento?.nota]);
  if (!agenzia) return null;
  const tel = linkChiama(agenzia.telefono);
  // qui si legge solo la dichiarazione (Dichiarata ✓ / In attesa / Non dichiara): è quella che conta per proporre e
  // per il punteggio. La collaborazione resta nella scheda dell'agenzia, dove si cambia.
  const dich = dichiarazioneAgenzia(agenzia.dichiarazione);
  const salva = async () => {
    setAttesa(true);
    try { await axios.put(`${API}/immobili/${immobile.id}/commento-agenzia`, dati); toast.success('Commento salvato (solo per noi)'); onFatto(); } catch (e) { errore(e); } finally { setAttesa(false); }
  };
  const cambiato = dati.feedback !== (commento?.feedback || '') || dati.nota !== (commento?.nota || '');
  return (
    <section className="card" data-testid="agenzia-staff">
      <div className="ttl"><div className="i b"><Icona nome="edificio" /></div><h2>Agenzia</h2>
        {isAdmin && agenzia.id && <Link to={`/admin/agenzie/${agenzia.id}`}>Scheda →</Link>}</div>
      <div className="agenzia-imm">
        <LogoAgenzia ag={agenzia} />
        <div style={{ minWidth: 0 }}>
          <b>{agenzia.etichetta || agenzia.nome}</b>
          {/* agenzia che vende aste di mestiere: i suoi annunci non si propongono a nessuno (campo `aste`) */}
          {agenzia.aste && <span className="tag t-r" style={{ marginLeft: '.3rem' }} data-testid="agenzia-aste">Aste</span>}
          <span>{[agenzia.referente?.nome, agenzia.telefono, agenzia.referente?.nota].filter(Boolean).join(' · ') || 'nessun contatto'}</span>
          <div className="imp-azioni" style={{ marginTop: '.35rem' }}>
            <span className={`dich ${dich.classe}`} data-testid="dichiarazione-agenzia">{dich.testo}</span>
          </div>
        </div>
        {tel && <a className="btn sm" href={tel}>Chiama</a>}
      </div>
      <div className="commento-agenzia">
        <p className="imp-gruppo">Com'è andata con loro per questa casa <SoloPerNoi /></p>
        <div className="imp-azioni" style={{ marginTop: 0 }}>
          <select className="imp-input" style={{ width: 'auto' }} value={dati.feedback} onChange={(e) => setDati({ ...dati, feedback: e.target.value })} aria-label="Giudizio sull'agenzia">
            {FEEDBACK.map(([k, n]) => <option key={k} value={k}>{n}</option>)}
          </select>
          <input className="imp-input" style={{ flex: 1, minWidth: '12rem', width: 'auto' }} value={dati.nota} placeholder="es. rispondono solo la mattina"
            onChange={(e) => setDati({ ...dati, nota: e.target.value })} aria-label="Commento sull'agenzia" />
          <button type="button" className="btn sm" disabled={!cambiato || attesa} onClick={salva}>Salva</button>
        </div>
      </div>
      {(agenzia.note || []).length > 0 && (
        <>
          <p className="imp-gruppo">Note interne sull'agenzia</p>
          {agenzia.note.slice(0, 3).map((n, i) => <div className="note" key={i}>{n.testo}{n.autore && <small>{n.autore}{n.data ? ` · ${n.data}` : ''}</small>}</div>)}
        </>
      )}
    </section>
  );
};

// ---------------------------------------------------------------- note interne (solo per noi)

export const NoteImmobile = ({ immobile, note, onFatto }) => {
  const [testo, setTesto] = useState('');
  const [attesa, setAttesa] = useState(false);
  const salva = async () => {
    if (!testo.trim()) return;
    setAttesa(true);
    try { await axios.post(`${API}/immobili/${immobile.id}/note`, { testo }); setTesto(''); onFatto(); } catch (e) { errore(e, 'Nota non salvata'); } finally { setAttesa(false); }
  };
  return (
    <section className="card" data-testid="note-immobile">
      <div className="ttl"><div className="i"><Icona nome="documento" /></div><h2>Note interne</h2><SoloPerNoi /></div>
      <div className="campo-nota" style={{ marginBottom: '.7rem' }}>
        <textarea value={testo} onChange={(e) => setTesto(e.target.value)} rows={2} placeholder="Il cliente non la vede mai" aria-label="Nuova nota interna" />
        <button type="button" className="btn sm p" disabled={!testo.trim() || attesa} onClick={salva}>Salva</button>
      </div>
      {!note.length && <p className="vuoto">Nessuna nota.</p>}
      {note.map((n, i) => <div className="note" key={i}>{n.testo}{(n.autore || n.data) && <small>{[n.autore, n.data].filter(Boolean).join(' · ')}</small>}</div>)}
    </section>
  );
};

// ---------------------------------------------------------------- prezzo e zona (staff: con lo scostamento)

export const PrezzoZonaImmobile = ({ immobile }) => {
  const zona = usePrezzoZona(immobile.id);
  const variazioni = variazioniPrezzo(immobile.storico_prezzi);
  return (
    <section className="card" data-testid="prezzo-zona-staff">
      <div className="ttl"><div className="i w"><Icona nome="euro" /></div><h2>Prezzo e zona</h2></div>
      {variazioni.length > 0 && (
        <div className="feed" style={{ marginBottom: '.8rem' }}>
          {variazioni.map((v, i) => <div key={i}><i className={`dot ${v.giu ? 'g' : 'k'}`} /><div><b>{v.testo}</b></div></div>)}
        </div>
      )}
      {!variazioni.length && <p className="imp-nota" style={{ marginBottom: '.6rem' }}>Prezzo mai cambiato dall'inserimento.</p>}
      <div className="kv kv4 due">
        <div><small>Questa casa</small><b>{zona?.mq_immobile ? euroMq(zona.mq_immobile) : '—'}</b></div>
        <div><small>Media di zona</small><b>{zona?.zona?.media ? euroMq(zona.zona.media) : '—'}</b></div>
      </div>
      {zona?.scostamento_zona != null && (
        <p className={`scostamento ${zona.scostamento_zona > 10 ? 'no' : zona.scostamento_zona < -3 ? 'g' : ''}`} data-testid="scostamento">{testoScostamento(zona.scostamento_zona)}</p>
      )}
      <p className="imp-nota" style={{ marginTop: '.5rem' }}>
        {zona?.zona?.media ? `${testoZona(zona.zona)}. ` : 'Poche case vicine per una media di zona. '}
        {zona?.omi ? `${testoOmi(zona.omi)}${zona.scostamento_omi != null ? `, ${testoScostamento(zona.scostamento_omi, 'il centro OMI')}` : ''}. Fonte: ${zona.omi.fonte}.` : 'Riferimento OMI non disponibile.'}
      </p>
    </section>
  );
};

// ---------------------------------------------------------------- se la compra

export const SeLaCompra = ({ stima, nome }) => {
  if (!stima?.disponibile) return null;
  const tasso = stima.tasso.toLocaleString('it-IT', { minimumFractionDigits: 1, maximumFractionDigits: 2 });
  return (
    <section className="card" data-testid="se-la-compra">
      <div className="ttl"><div className="i"><Icona nome="casa" /></div><h2>Se {nome ? `${nome} la compra` : 'la compra'}</h2></div>
      <div className="kv kv4 due">
        <div><small>Rata al mese</small><b>{formatEuro(stima.rata)}</b></div>
        <div><small>Anticipo</small><b>{formatEuro(stima.anticipo)}</b></div>
      </div>
      <p className="imp-nota" style={{ marginTop: '.6rem' }}>Su {formatEuro(stima.prezzo)}{stima.da_proposta ? ' (la proposta)' : ''}, mutuo {stima.percentuale}%, {stima.anni} anni,
        {stima.tasso_di_riserva ? ` tasso indicativo ${tasso}%` : ` tasso di oggi ${tasso}%`}. Lo stesso calcolo che vede il cliente.</p>
    </section>
  );
};

// ---------------------------------------------------------------- proponi ad altri clienti

// perché lo proponiamo: cerca ancora casa (fase Ricerca o Visita) o è fermo da un po'
const MOTIVO_CLIENTE = { ricerca: 'in ricerca', visita: 'in visita', fermo: 'fermo' };

export const ProponiAdAltri = ({ immobile, onFatto }) => {
  const [dati, setDati] = useState(null);
  const [fatti, setFatti] = useState({});
  const [attesa, setAttesa] = useState(null);
  const [asta, setAsta] = useState(immobile.asta === true);
  const carica = useCallback(() => axios.get(`${API}/immobili/${immobile.id}/clienti-compatibili`)
    .then((r) => setDati(r.data)).catch(() => setDati({ clienti: [], totale: 0 })), [immobile.id]);
  useEffect(() => { carica(); }, [carica]);
  useEffect(() => { setAsta(immobile.asta === true); }, [immobile.asta]);
  const proponi = async (c) => {
    setAttesa(c.id);
    try {
      const r = await axios.post(`${API}/clienti/${c.id}/archivio/${immobile.id}/proponi`);
      setFatti((f) => ({ ...f, [c.id]: r.data.immobile_id }));
      toast.success(`Proposta a ${c.nome}: gli arriva «Volete vederla?»`);
    } catch (e) { errore(e); } finally { setAttesa(null); }
  };
  if (!dati) return null;
  const daVerificare = Boolean(dati.verifica && !dati.verifica.proponibile);
  return (
    <section className="card" data-testid="proponi-ad-altri">
      {/* «aste escluse»: un'asta giudiziaria non si propone e non si suggerisce mai (backend/aste.py) */}
      <div className="ttl"><div className="i v"><Icona nome="persone" /></div><h2>Proponi ad altri clienti <small>{dati.totale || 0} · aste escluse</small></h2>
        {/* se il filtro l'ha lasciata passare la segna lo staff: esce da tutte le proposte e l'agenzia viene
            contata. Se l'hanno segnata per sbaglio, «Non è un'asta» rimette tutto a posto */}
        {asta ? (
          <>
            <span className="tag t-r" data-testid="gia-asta">Asta</span>
            <BottoneNonAsta immobileId={immobile.id} taglia="xs" onFatto={() => { setAsta(false); carica(); if (onFatto) onFatto(); }} />
          </>
        ) : <BottoneAsta immobileId={immobile.id} onFatto={() => { setAsta(true); carica(); if (onFatto) onFatto(); }} />}</div>
      <p className="imp-nota" style={{ marginBottom: '.6rem' }}>Clienti che cercano ancora casa — in ricerca o in visita (o fermi) — per cui va bene, con punteggio da 60 in su: tutta la loro ricerca (budget, comuni e confinanti, tipo, locali, bagni, metri, piano, giardino, terrazzo, box). Non sanno di chi era la casa.</p>
      {!dati.clienti.length && <p className="vuoto">{dati.motivo === 'non_proponibile'
        ? 'Non si propone ad altri: è in trattativa o al rogito, l\'annuncio è ritirato o è stata scartata per com\'è la casa.'
        : 'Nessun altro cliente compatibile.'}</p>}
      <div className="imp-righe">
        {dati.clienti.map((c) => (
          <div key={c.id} data-testid={`compatibile-${c.id}`}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Link className="nome-link" to={`/admin/clienti/${c.id}`}>{c.nome}</Link>
              <span className={`tag ${c.motivo === 'fermo' ? 't-r' : 't-g'}`} style={{ marginLeft: '.4rem' }}>{MOTIVO_CLIENTE[c.motivo] || 'in ricerca'}</span>
              <span className="descr" style={{ display: 'block' }}>{[c.budget ? `budget ${euro(c.budget)}` : null, c.zone, c.segue ? `segue ${c.segue}` : null].filter(Boolean).join(' · ')}</span>
              <PercheManca punteggio={c.punteggio} perche={c.perche} manca={c.manca} />
            </div>
            <div className="azioni">
              {/* una casa che non controlliamo da più di una settimana si verifica prima: il bottone resta spento
                  finché l'estensione non dice che c'è ancora (il server rifiuterebbe comunque la proposta) */}
              {fatti[c.id]
                ? <Link className="btn xs" to={`/admin/clienti/${c.id}?evidenzia=${fatti[c.id]}#immobili`}>Proposta ✓</Link>
                : <button type="button" className="btn xs p" disabled={attesa === c.id || daVerificare} title={daVerificare ? dati.verifica.testo : undefined}
                  onClick={() => proponi(c)}>{attesa === c.id ? 'Propongo…' : daVerificare ? 'Verifica in corso' : 'Proponi'}</button>}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
